import React, { useEffect, useState, useMemo } from 'react';
import { Edit, Trash2, AlertCircle, Info } from 'lucide-react';
import { Toastify } from 'lift-ui';
import { apiClientDrawerV2 as apiClientV2 } from '@lift-admin/utils';
import apiClient from '@lift-admin/utils/apiClient';
import { logAdminAction } from '@lift-admin/utils/actionLogTrack';
import {
  buildDetails,
  buildSummary,
  diffFields,
  type FieldSpec,
} from '@lift-admin/utils/logChangeFormatter';
import { generateUUID } from "@lift-admin/utils/uuid";
import {
  Button,
  DataTable,
  Badge,
  SpinnerLoader,
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetFooter,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  Label,
  SingleSelect,
} from 'lift-ui';
import type { TableColumn } from 'lift-ui';
import { flattenNamedEntities } from '@/features/drawer-structure-v2/drawer-parts/accessLogHelpers';

interface DefaultSidebarItem {
  id: string;
  application_id: string;
  item_id: string;
  user_id?: string;
  brand_id?: string;
  organization_id?: string;
  application_name?: string;
  item_name?: string;
  user_name?: string;
  brand_name?: string;
  organization_name?: string;
  is_active?: boolean;
  created_at?: string;
  updated_at?: string;
  created_by?: string;
  modified_by?: string;
}

interface DefaultSidebarItemUpsert {
  application_id: string;
  item_id: string;
  user_id?: string;
  brand_id?: string;
  organization_id?: string;
}

interface Application {
  id: string;
  name: string;
  display_name: string;
}

interface Item {
  id: string;
  name: string;
  display_name: string;
  application_id: string;
}

interface DefaultSidebarItemsProps {
  searchText: string;
  toggle: string;
  openDialog: boolean;
  setOpenDialog: (value: boolean) => void;
  hasEditAccess?: boolean;
  hasManageAccess?: boolean;
}

const DefaultSidebarItems: React.FC<DefaultSidebarItemsProps> = ({
  searchText,
  toggle,
  openDialog,
  setOpenDialog,
  hasEditAccess = true,
  hasManageAccess = true,
}) => {
  const showActionsColumn = hasEditAccess || hasManageAccess;
  // State for data
  const [defaultItems, setDefaultItems] = useState<DefaultSidebarItem[]>([]);
  const [allDefaultItems, setAllDefaultItems] = useState<DefaultSidebarItem[]>([]);
  const [applications, setApplications] = useState<Application[]>([]);
  const [items, setItems] = useState<Item[]>([]);
  const [brands, setBrands] = useState<any[]>([]);
  const [organizations, setOrganizations] = useState<any[]>([]);
  const [filteredItems, setFilteredItems] = useState<Item[]>([]);

  // State for UI
  const [loading, setLoading] = useState(false);
  const [editingItem, setEditingItem] = useState<DefaultSidebarItem | null>(null);
  const [formData, setFormData] = useState<DefaultSidebarItemUpsert>({
    application_id: '',
    item_id: '',
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [filterApplication, setFilterApplication] = useState('');

  // Pagination
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [totalCount, setTotalCount] = useState(0);

  // Add state for scope selection
  const [scope, setScope] = useState<string>('');

  // Add state for delete dialog
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [itemToDelete, setItemToDelete] = useState<DefaultSidebarItem | null>(null);

  // Add state for filtering (single select to match drawer items, reports, sandbox)
  const [filterApplicationId, setFilterApplicationId] = useState<string>('');
  const [filterOrganizationId, setFilterOrganizationId] = useState<string>('');
  const [filterBrandId, setFilterBrandId] = useState<string>('');

  // Add state for item details
  const [itemDetails, setItemDetails] = useState<any>({});

  // Add state for auto-determined scope
  const [isAutoScope, setIsAutoScope] = useState<boolean>(false);
  const [autoScopeMessage, setAutoScopeMessage] = useState<string>('');

  // Add state for exclude mode handling
  const [isExcludeMode, setIsExcludeMode] = useState<boolean>(false);
  const [excludedBrands, setExcludedBrands] = useState<any[]>([]);
  const [excludedOrganizations, setExcludedOrganizations] = useState<any[]>([]);
  const [excludedBrandItems, setExcludedBrandItems] = useState<Record<string, string>>({});
  const [excludedOrgItems, setExcludedOrgItems] = useState<Record<string, string>>({});

  // Loading states
  const [loadingApplications, setLoadingApplications] = useState(false);
  const [loadingItems, setLoadingItems] = useState(false);
  const [loadingBrands, setLoadingBrands] = useState(false);
  const [loadingOrganizations, setLoadingOrganizations] = useState(false);
  const [loadingDefaultItems, setLoadingDefaultItems] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  // Load data on component mount
  useEffect(() => {
    loadApplications();
    loadBrandsAndOrganizations();
  }, []);

  // Fetch when search changes (filters applied client-side)
  useEffect(() => {
    loadDefaultItems();
  }, [searchText]);

  // Initialize form when dialog opens from header
  useEffect(() => {
    if (openDialog && !editingItem) {
      setFormData({
        application_id: '',
        item_id: '',
      });
      setErrors({});
      setScope('');
      setIsAutoScope(false);
      setAutoScopeMessage('');
      setIsExcludeMode(false);
      setExcludedBrands([]);
      setExcludedOrganizations([]);
      setExcludedBrandItems({});
      setExcludedOrgItems({});
    }
  }, [openDialog, editingItem]);

  // Reset pagination when filters or search change
  useEffect(() => {
    setPage(0);
  }, [filterApplicationId, filterOrganizationId, filterBrandId, searchText]);

  // Load items when application changes
  useEffect(() => {
    if (formData.application_id) {
      loadItemsForApplication(formData.application_id);
    } else {
      setItems([]);
      setFilteredItems([]);
    }
  }, [formData.application_id]);

  // Function to load brands and organizations for filters
  const loadBrandsAndOrganizations = async () => {
    try {
      setLoadingBrands(true);
      setLoadingOrganizations(true);

      const brandsRes = await apiClient.get(`/brand/v1/admin/brands/all`);
      setBrands(brandsRes.data.response || []);

      const orgsRes = await apiClient.get(`/brand/v1/organizations/all`);
      setOrganizations(orgsRes.data.response || []);
    } catch (error) {
      console.error('Error loading brands and organizations:', error);
      Toastify('error', 'Failed to load brands and organizations');
      setBrands([]);
      setOrganizations([]);
    } finally {
      setLoadingBrands(false);
      setLoadingOrganizations(false);
    }
  };

  // Filter items based on search text
  useEffect(() => {
    if (searchText) {
      loadDefaultItems();
    }
  }, [searchText]);

  // Load item details for display
  const loadItemDetails = async (itemIds: string[]) => {
    try {
      const user = JSON.parse(localStorage.getItem('user') || '{}');
      const response = await apiClientV2.get('/admin/items', {
        params: {
          include_inactive: true,
          user_id: user.id,
          is_admin: user.is_admin || false,
          is_team: user.is_team || false,
        }
      });

      const items = response.data || [];
      const detailsMap: any = {};
      items.forEach((item: any) => {
        detailsMap[item.id] = item;
      });
      setItemDetails(detailsMap);
    } catch (error) {
      console.error('Error loading item details:', error);
    }
  };

  // Update loadDefaultItems to include filters and load item details
  const loadDefaultItems = async () => {
    try {
      setLoadingDefaultItems(true);
      const user = JSON.parse(localStorage.getItem('user') || '{}');

      const params: any = {
        user_id: user.id,
        is_admin: user.is_admin || false,
        is_team: user.is_team || false,
      };

      if (user.brand_id !== undefined && user.brand_id !== null && user.brand_id !== "") {
        params.brand_id = user.brand_id;
      }

      if (searchText) {
        params.search = searchText;
      }

      const response = await apiClientV2.get('/admin/default-items', { params });
      const items = response.data || [];

      const filteredByUser = items.filter((item: any) => !item.user_id);
      setAllDefaultItems(filteredByUser);
    } catch (error) {
      console.error('Error loading default items:', error);
      Toastify('error', 'Failed to load default sidebar items');
      setDefaultItems([]);
    } finally {
      setLoadingDefaultItems(false);
    }
  };

  // Apply single-select filters client-side and paginate
  const filteredDefaultItems = useMemo(() => {
    let list = allDefaultItems;
    if (filterApplicationId) {
      list = list.filter((item: any) => String(item.application_id) === filterApplicationId);
    }
    if (filterOrganizationId) {
      list = list.filter((item: any) => String(item.organization_id || '') === filterOrganizationId);
    }
    if (filterBrandId) {
      list = list.filter((item: any) => String(item.brand_id || '') === filterBrandId);
    }
    return list;
  }, [allDefaultItems, filterApplicationId, filterOrganizationId, filterBrandId]);

  // --- Progressive filter options ---
  // Application: only apps that appear in the table
  const availableApplicationOptions = useMemo(() => {
    const appIds = new Set(allDefaultItems.map((item: any) => String(item.application_id)).filter(Boolean));
    return applications
      .filter((a: any) => appIds.has(String(a.id)))
      .map((a: any) => ({ label: (a.display_name || a.name) ?? String(a.id), value: String(a.id) }));
  }, [allDefaultItems, applications]);

  // Items after app filter (for deriving org options)
  const itemsAfterAppFilter = useMemo(() => {
    if (!filterApplicationId) return allDefaultItems;
    return allDefaultItems.filter((item: any) => String(item.application_id) === filterApplicationId);
  }, [allDefaultItems, filterApplicationId]);

  // Organization: only orgs present in items after app filter
  const availableOrganizationOptions = useMemo(() => {
    const orgMap = new Map<string, string>();
    itemsAfterAppFilter.forEach((item: any) => {
      if (item.organization_id) {
        const org = organizations.find((o: any) => String(o.id) === String(item.organization_id));
        orgMap.set(String(item.organization_id), org?.name ?? String(item.organization_id));
      }
    });
    return Array.from(orgMap.entries()).map(([value, label]) => ({ label, value }));
  }, [itemsAfterAppFilter, organizations]);

  // Items after app + org filter (for deriving brand options)
  const itemsAfterAppOrgFilter = useMemo(() => {
    if (!filterOrganizationId) return itemsAfterAppFilter;
    return itemsAfterAppFilter.filter((item: any) => String(item.organization_id || '') === filterOrganizationId);
  }, [itemsAfterAppFilter, filterOrganizationId]);

  // Brand: only brands present in items after app + org filter
  const availableBrandOptions = useMemo(() => {
    const brandMap = new Map<string, string>();
    itemsAfterAppOrgFilter.forEach((item: any) => {
      if (item.brand_id) {
        const brand = brands.find((b: any) => String(b.id) === String(item.brand_id));
        brandMap.set(String(item.brand_id), brand?.name ?? String(item.brand_id));
      }
    });
    return Array.from(brandMap.entries()).map(([value, label]) => ({ label, value }));
  }, [itemsAfterAppOrgFilter, brands]);

  useEffect(() => {
    setTotalCount(filteredDefaultItems.length);
    const startIndex = page * rowsPerPage;
    const endIndex = startIndex + rowsPerPage;
    const paginatedItems = filteredDefaultItems.slice(startIndex, endIndex);
    setDefaultItems(paginatedItems);
    const itemIds = paginatedItems.map((item: any) => item.item_id);
    if (itemIds.length > 0) loadItemDetails(itemIds);
  }, [filteredDefaultItems, page, rowsPerPage]);

  const loadApplications = async () => {
    try {
      setLoadingApplications(true);
      const user = JSON.parse(localStorage.getItem('user') || '{}');

      const response = await apiClientV2.get('/admin/applications', {
        params: {
          include_inactive: true,
          user_id: user.id,
          is_admin: true,
          is_team: false,
        }
      });
      setApplications(response.data || []);
    } catch (error) {
      console.error('Error loading applications:', error);
      Toastify('error', 'Failed to load applications');
    } finally {
      setLoadingApplications(false);
    }
  };

  const loadItemsForApplication = async (applicationId: string) => {
    if (!applicationId) {
      setItems([]);
      setFilteredItems([]);
      return;
    }

    try {
      setLoadingItems(true);
      const user = JSON.parse(localStorage.getItem('user') || '{}');

      const channelsResponse = await apiClientV2.get('/admin/channels', {
        params: {
          include_inactive: true,
          user_id: user.id,
          is_admin: true,
          is_team: false,
        }
      });

      const applicationChannels = (channelsResponse.data || []).filter(
        (channel: any) => channel.application_id === applicationId
      );

      const channelIds = applicationChannels.map((channel: any) => channel.id);

      const itemsResponse = await apiClientV2.get('/admin/items', {
        params: {
          include_inactive: true,
          user_id: user.id,
          is_admin: true,
          is_team: false,
        }
      });

      const filtered = (itemsResponse.data || []).filter(
        (item: any) => channelIds.includes(item.channel_id)
      );

      setItems(filtered);
      setFilteredItems(filtered);
    } catch (error) {
      console.error('Error loading items:', error);
      Toastify('error', 'Failed to load items for application');
      setItems([]);
      setFilteredItems([]);
    } finally {
      setLoadingItems(false);
    }
  };

  // Function to check item access and auto-set scope
  const checkItemAccessAndSetScope = (selectedItemId: string, editingItem?: DefaultSidebarItem) => {
    setIsAutoScope(false);
    setAutoScopeMessage('');
    setIsExcludeMode(false);
    setExcludedBrands([]);
    setExcludedOrganizations([]);
    setExcludedBrandItems({});
    setExcludedOrgItems({});

    const selectedItemDetails = itemDetails[selectedItemId];
    if (!selectedItemDetails || !selectedItemDetails.access) {
      return;
    }

    const access = selectedItemDetails.access;

    if (access.brand_level &&
      access.brand_level.brands &&
      Array.isArray(access.brand_level.brands) &&
      access.brand_level.brands.length > 0 &&
      access.brand_level.access_mode === 'INCLUDE') {

      const brandNames = access.brand_level.brands.map((brand: any) => brand.name).join(', ');

      setScope('brand');
      setIsAutoScope(true);
      setAutoScopeMessage(`This item has brand-level access configuration with INCLUDE mode. Brand scope is automatically selected and required. Included brands: ${brandNames}`);

      setFormData(prev => ({
        ...prev,
        organization_id: '',
        user_id: ''
      }));
      return;
    }

    if (access.brand_level &&
      access.brand_level.brands &&
      Array.isArray(access.brand_level.brands) &&
      access.brand_level.brands.length > 0 &&
      access.brand_level.access_mode === 'EXCLUDE') {

      const brandNames = access.brand_level.brands.map((brand: any) => brand.name).join(', ');

      setIsExcludeMode(true);
      setExcludedBrands(access.brand_level.brands);
      setAutoScopeMessage(`This item has brand-level access configuration with EXCLUDE mode. Select a specific scope for targeted access, or leave empty for application-wide with excluded brand options. Excluded brands: ${brandNames}`);

      const initialBrandItems: Record<string, string> = {};
      access.brand_level.brands.forEach((brand: any) => {
        initialBrandItems[brand.id] = '';
      });
      setExcludedBrandItems(initialBrandItems);
    }

    if (access.organization_level &&
      access.organization_level.organizations &&
      Array.isArray(access.organization_level.organizations) &&
      access.organization_level.organizations.length > 0 &&
      access.organization_level.access_mode === 'INCLUDE') {

      const orgNames = access.organization_level.organizations.map((org: any) => org.name).join(', ');

      setScope('organization');
      setIsAutoScope(true);
      setAutoScopeMessage(`This item has organization-level access configuration with INCLUDE mode. Organization scope is automatically selected and required. Included organizations: ${orgNames}`);

      setFormData(prev => ({
        ...prev,
        brand_id: '',
        user_id: ''
      }));
      return;
    }

    if (access.organization_level &&
      access.organization_level.organizations &&
      Array.isArray(access.organization_level.organizations) &&
      access.organization_level.organizations.length > 0 &&
      access.organization_level.access_mode === 'EXCLUDE') {

      const orgNames = access.organization_level.organizations.map((org: any) => org.name).join(', ');

      setIsExcludeMode(true);
      setExcludedOrganizations(access.organization_level.organizations);
      setAutoScopeMessage(`This item has organization-level access configuration with EXCLUDE mode. Select a specific scope for targeted access, or leave empty for application-wide with excluded organization options. Excluded organizations: ${orgNames}`);

      const initialOrgItems: Record<string, string> = {};
      access.organization_level.organizations.forEach((org: any) => {
        initialOrgItems[org.id] = '';
      });
      setExcludedOrgItems(initialOrgItems);
    }

    if (editingItem && (excludedBrands.length > 0 || excludedOrganizations.length > 0)) {
      setTimeout(() => {
        const brandItems: Record<string, string> = {};
        excludedBrands.forEach(brand => {
          const existingItem = defaultItems.find(item =>
            String(item.brand_id) === String(brand.id) &&
            item.application_id === editingItem.application_id
          );
          if (existingItem) {
            brandItems[brand.id] = existingItem.item_id;
          }
        });

        const orgItems: Record<string, string> = {};
        excludedOrganizations.forEach(org => {
          const existingItem = defaultItems.find(item =>
            String(item.organization_id) === String(org.id) &&
            item.application_id === editingItem.application_id
          );
          if (existingItem) {
            orgItems[org.id] = existingItem.item_id;
          }
        });

        if (Object.keys(brandItems).length > 0) {
          setExcludedBrandItems(prev => ({ ...prev, ...brandItems }));
        }
        if (Object.keys(orgItems).length > 0) {
          setExcludedOrgItems(prev => ({ ...prev, ...orgItems }));
        }
      }, 0);
    }
  };

  // Helper function to check if brand is in item's access list
  const isBrandInAccessList = (itemId: string, brandId: string): boolean => {
    const itemDetail = itemDetails[itemId];
    if (!itemDetail?.access?.brand_level?.brands) return false;

    return itemDetail.access.brand_level.brands.some((brand: any) => brand.id === brandId);
  };

  // Helper function to check if organization is in item's access list
  const isOrganizationInAccessList = (itemId: string, organizationId: string): boolean => {
    const itemDetail = itemDetails[itemId];
    if (!itemDetail?.access?.organization_level?.organizations) return false;

    return itemDetail.access.organization_level.organizations.some((org: any) => org.id === organizationId);
  };

  const resolveApplicationName = (applicationId?: string | null) => {
    if (!applicationId) return '';
    const app = applications.find((a) => String(a.id) === String(applicationId));
    return (
      app?.display_name ||
      app?.name ||
      flattenNamedEntities(applications as any[]).find(
        (e) => String(e.id) === String(applicationId)
      )?.name ||
      `Application ${applicationId}`
    );
  };

  const resolveItemName = (itemId?: string | null) => {
    if (!itemId) return '';
    const fromItems =
      items.find((i) => String(i.id) === String(itemId)) ||
      filteredItems.find((i) => String(i.id) === String(itemId));
    const fromDetails = itemDetails?.[itemId as string];
    return (
      fromItems?.display_name ||
      fromItems?.name ||
      fromDetails?.display_name ||
      fromDetails?.name ||
      `Item ${itemId}`
    );
  };

  const resolveBrandName = (brandId?: string | null) => {
    if (!brandId) return '';
    return (
      flattenNamedEntities(brands).find((b) => String(b.id) === String(brandId))
        ?.name || `Brand ${brandId}`
    );
  };

  const resolveOrganizationName = (organizationId?: string | null) => {
    if (!organizationId) return '';
    return (
      flattenNamedEntities(organizations).find(
        (o) => String(o.id) === String(organizationId)
      )?.name || `Organization ${organizationId}`
    );
  };

  const resolveScopeLabel = (data: {
    brand_id?: string | null;
    organization_id?: string | null;
    scope?: string | null;
  }) => {
    if (data.brand_id) return 'Brand';
    if (data.organization_id) return 'Organization';
    if (data.scope === 'brand') return 'Brand';
    if (data.scope === 'organization') return 'Organization';
    return 'Application-wide';
  };

  const getDefaultSidebarFieldSpecs = (): FieldSpec[] => [
    {
      key: 'application_id',
      label: 'Application',
      resolve: (id) => resolveApplicationName(id) || 'Not Set',
    },
    {
      key: 'item_id',
      label: 'Item',
      resolve: (id) => resolveItemName(id) || 'Not Set',
    },
    {
      key: 'brand_id',
      label: 'Brand',
      resolve: (id) => (id ? resolveBrandName(id) : 'Not Set'),
    },
    {
      key: 'organization_id',
      label: 'Organization',
      resolve: (id) => (id ? resolveOrganizationName(id) : 'Not Set'),
    },
    {
      key: 'scope',
      label: 'Scope',
      resolve: (value) => String(value || 'Application-wide'),
    },
  ];

  const buildExcludeAssignmentDetails = (): Array<{ label: string; value: string }> => {
    const details: Array<{ label: string; value: string }> = [];
    for (const brand of excludedBrands) {
      const selectedItemId = excludedBrandItems[brand.id];
      if (!selectedItemId) continue;
      details.push({
        label: `Brand override (${resolveBrandName(brand.id) || brand.name || brand.id})`,
        value: resolveItemName(selectedItemId),
      });
    }
    for (const org of excludedOrganizations) {
      const selectedItemId = excludedOrgItems[org.id];
      if (!selectedItemId) continue;
      details.push({
        label: `Organization override (${resolveOrganizationName(org.id) || org.name || org.id})`,
        value: resolveItemName(selectedItemId),
      });
    }
    return details;
  };

  // Helper function to add brand to access list
  const addBrandToAccessList = async (brandId: string, itemId: string, userId: string) => {
    const payload = [{
      brand_id: parseInt(brandId),
      level_type: "item",
      level_ids: [itemId],
      access_mode: "INCLUDE",
      is_wildcard: false,
      action: "add",
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString(),
      created_by: parseInt(userId),
      modified_by: parseInt(userId),
    }];

    const brandName = resolveBrandName(brandId);
    const itemName = resolveItemName(itemId);

    try {
      const res = await apiClientV2.post(`/admin/brand-access`, {
        data: payload,
      }, {
        params: {
          user_id: userId,
        }
      });

      await logAdminAction(
        `Assigned brand "${brandName}" access to item "${itemName}"`,
        {
          url: res.config?.url || '/admin/brand-access',
          method: (res.config?.method || 'POST').toUpperCase(),
          status: res.status,
          category: 'drawer',
          action: 'assign_brand_access',
          brandId,
          brandName,
          itemId,
          itemName,
          details: [
            { label: 'Brand', value: brandName },
            { label: 'Item', value: itemName },
            { label: 'Access Mode', value: 'INCLUDE' },
          ],
        }
      );
    } catch (error: any) {
      console.error('Error adding brand access:', error);
      await logAdminAction(
        `Failed to assign brand "${brandName}" access to item "${itemName}": ${
          error?.response?.data?.detail || error?.message || 'Unknown error'
        }`,
        {
          url: '/admin/brand-access',
          method: 'POST',
          status: error?.response?.status,
          category: 'drawer',
          action: 'assign_brand_access',
          brandId,
          brandName,
          itemId,
          itemName,
        },
        true
      );
      throw error;
    }
  };

  // Helper function to add organization to access list
  const addOrganizationToAccessList = async (organizationId: string, itemId: string, userId: string) => {
    const payload = [{
      organization_id: parseInt(organizationId),
      level_type: "item",
      level_ids: [itemId],
      access_mode: "INCLUDE",
      is_wildcard: false,
      action: "add",
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString(),
      created_by: parseInt(userId),
      modified_by: parseInt(userId),
    }];

    const organizationName = resolveOrganizationName(organizationId);
    const itemName = resolveItemName(itemId);

    try {
      const res = await apiClientV2.post(`/admin/organization-access`, {
        data: payload,
      }, {
        params: {
          user_id: userId,
        }
      });

      await logAdminAction(
        `Assigned organization "${organizationName}" access to item "${itemName}"`,
        {
          url: res.config?.url || '/admin/organization-access',
          method: (res.config?.method || 'POST').toUpperCase(),
          status: res.status,
          category: 'drawer',
          action: 'assign_organization_access',
          organizationId,
          organizationName,
          itemId,
          itemName,
          details: [
            { label: 'Organization', value: organizationName },
            { label: 'Item', value: itemName },
            { label: 'Access Mode', value: 'INCLUDE' },
          ],
        }
      );
    } catch (error: any) {
      console.error('Error adding organization access:', error);
      await logAdminAction(
        `Failed to assign organization "${organizationName}" access to item "${itemName}": ${
          error?.response?.data?.detail || error?.message || 'Unknown error'
        }`,
        {
          url: '/admin/organization-access',
          method: 'POST',
          status: error?.response?.status,
          category: 'drawer',
          action: 'assign_organization_access',
          organizationId,
          organizationName,
          itemId,
          itemName,
        },
        true
      );
      throw error;
    }
  };

  // Helper function to get available items for an excluded brand
  const getAvailableItemsForBrand = (brandId: string): Item[] => {
    return items.filter(item => {
      const itemDetail = itemDetails[item.id];

      if (!itemDetail?.access?.brand_level?.brands) return true;

      const brandAccess = itemDetail.access.brand_level;
      const brandInAccess = brandAccess.brands.some((brand: any) => brand.id === brandId);

      if (brandAccess.access_mode === 'INCLUDE' && brandInAccess) return true;
      if (brandAccess.access_mode === 'EXCLUDE' && !brandInAccess) return true;

      return false;
    });
  };

  // Helper function to get available items for an excluded organization
  const getAvailableItemsForOrganization = (organizationId: string): Item[] => {
    return items.filter(item => {
      const itemDetail = itemDetails[item.id];

      if (!itemDetail?.access?.organization_level?.organizations) return true;

      const orgAccess = itemDetail.access.organization_level;
      const orgInAccess = orgAccess.organizations.some((org: any) => org.id === organizationId);

      if (orgAccess.access_mode === 'INCLUDE' && orgInAccess) return true;
      if (orgAccess.access_mode === 'EXCLUDE' && !orgInAccess) return true;

      return false;
    });
  };

  // Helper function to get available brands (excluding excluded ones)
  const getAvailableBrands = (): any[] => {
    if (!isExcludeMode || excludedBrands.length === 0) return brands;
    const excludedBrandIds = excludedBrands.map(brand => String(brand.id));
    return brands.filter(brand => !excludedBrandIds.includes(String(brand.id)));
  };

  // Helper function to get available organizations (excluding excluded ones)
  const getAvailableOrganizations = (): any[] => {
    if (!isExcludeMode || excludedOrganizations.length === 0) return organizations;

    const excludedOrgIds = excludedOrganizations.map(org => String(org.id));
    return organizations.filter(org => !excludedOrgIds.includes(String(org.id)));
  };

  const handleCreate = () => {
    setEditingItem(null);
    setFormData({
      application_id: '',
      item_id: '',
    });
    setErrors({});
    setScope('');
    setIsAutoScope(false);
    setAutoScopeMessage('');
    setIsExcludeMode(false);
    setExcludedBrands([]);
    setExcludedOrganizations([]);
    setExcludedBrandItems({});
    setExcludedOrgItems({});
    setOpenDialog(true);
  };

  const handleEdit = async (item: DefaultSidebarItem) => {
    console.log('[handleEdit] Starting edit for item:', item.id, {
      application_id: item.application_id,
      item_id: item.item_id,
      brand_id: item.brand_id,
      organization_id: item.organization_id,
    });

    setErrors({});

    let itemScope = '';
    if (item.brand_id) {
      itemScope = 'brand';
    } else if (item.organization_id) {
      itemScope = 'organization';
    }
    console.log('[handleEdit] Determined scope:', itemScope);
    setScope(itemScope);

    setIsAutoScope(false);
    setAutoScopeMessage('');
    setIsExcludeMode(false);
    setExcludedBrands([]);
    setExcludedOrganizations([]);
    setExcludedBrandItems({});
    setExcludedOrgItems({});

    if (item.application_id) {
      console.log('[handleEdit] Loading items for application:', item.application_id);
      await loadItemsForApplication(item.application_id);
      console.log('[handleEdit] Items loaded, count:', items.length);
    }

    console.log('[handleEdit] Applications available:', applications.length, applications.map(a => ({ id: a.id, name: a.name })));
    console.log('[handleEdit] Setting formData and editingItem');

    setEditingItem(item);
    setFormData({
      application_id: String(item.application_id || ''),
      item_id: String(item.item_id || ''),
      user_id: String(item.user_id || ''),
      brand_id: String(item.brand_id || ''),
      organization_id: String(item.organization_id || ''),
    });
    console.log('[handleEdit] formData set:', {
      application_id: String(item.application_id || ''),
      item_id: String(item.item_id || ''),
      brand_id: String(item.brand_id || ''),
      organization_id: String(item.organization_id || ''),
    });

    if (item.item_id) {
      checkItemAccessAndSetScope(item.item_id, item);
    }

    setOpenDialog(true);
    console.log('[handleEdit] Dialog opened');
  };

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.application_id) {
      newErrors.application_id = 'Application is required';
    }

    if (!formData.item_id) {
      newErrors.item_id = 'Item is required';
    }

    const scopeFields = [formData.brand_id, formData.organization_id];
    const selectedScopes = scopeFields.filter(Boolean);

    if (selectedScopes.length > 1) {
      newErrors.scope = 'Please select only one scope (Brand or Organization)';
    }

    if (isAutoScope) {
      if (scope === 'brand' && !formData.brand_id) {
        newErrors.brand_id = 'Brand is required for this item due to its access configuration';
      }
      if (scope === 'organization' && !formData.organization_id) {
        newErrors.organization_id = 'Organization is required for this item due to its access configuration';
      }
    }

    if (isExcludeMode && !formData.brand_id && !formData.organization_id) {
      const brandItemsSelected = Object.values(excludedBrandItems).filter(Boolean).length;
      const orgItemsSelected = Object.values(excludedOrgItems).filter(Boolean).length;

      if (brandItemsSelected === 0 && orgItemsSelected === 0) {
        newErrors.excludeItems = 'Please select at least one item for excluded entities';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;
    try {
      setSubmitting(true);
      const user = JSON.parse(localStorage.getItem('user') || '{}');
      const params: any = {
        user_id: user.id,
        is_admin: user.is_admin || false,
        is_team: user.is_team || false,
      };
      if (editingItem) {
        if (isExcludeMode && !formData.brand_id && !formData.organization_id) {
          const promises: Promise<any>[] = [];

          for (const brand of excludedBrands) {
            const selectedItemId = excludedBrandItems[brand.id];
            if (selectedItemId) {
              const existingItem = defaultItems.find(item =>
                String(item.brand_id) === String(brand.id) &&
                item.application_id === formData.application_id
              );

              const brandPayload = {
                id: existingItem ? existingItem.id : generateUUID(),
                application_id: formData.application_id,
                item_id: selectedItemId,
                is_active: true,
                user_id: null,
                brand_id: brand.id,
                organization_id: null,
              };

              promises.push(apiClientV2.post('/admin/default-items', brandPayload, { params }));
            }
          }

          for (const org of excludedOrganizations) {
            const selectedItemId = excludedOrgItems[org.id];
            if (selectedItemId) {
              const existingItem = defaultItems.find(item =>
                String(item.organization_id) === String(org.id) &&
                item.application_id === formData.application_id
              );

              const orgPayload = {
                id: existingItem ? existingItem.id : generateUUID(),
                application_id: formData.application_id,
                item_id: selectedItemId,
                is_active: true,
                user_id: null,
                brand_id: null,
                organization_id: org.id,
              };

              promises.push(apiClientV2.post('/admin/default-items', orgPayload, { params }));
            }
          }

          await Promise.all(promises);
        } else {
          const payload: any = {
            id: editingItem.id,
            application_id: formData.application_id,
            item_id: formData.item_id,
            is_active: true,
            user_id: formData.user_id || null,
            brand_id: formData.brand_id || null,
            organization_id: formData.organization_id || null,
          };

          await apiClientV2.post(`/admin/default-items`, payload, { params });

          if (isAutoScope && formData.item_id) {
            try {
              if (scope === 'brand' && formData.brand_id) {
                if (!isBrandInAccessList(formData.item_id, formData.brand_id)) {
                  await addBrandToAccessList(formData.brand_id, formData.item_id, user.id);
                }
              }

              if (scope === 'organization' && formData.organization_id) {
                if (!isOrganizationInAccessList(formData.item_id, formData.organization_id)) {
                  await addOrganizationToAccessList(formData.organization_id, formData.item_id, user.id);
                }
              }
            } catch (accessError) {
              console.error('Error updating access list:', accessError);
              Toastify('warning', 'Default item updated but failed to update access list');
            }
          }
        }

        Toastify('success', 'Default sidebar item updated successfully');

        const fieldSpecs = getDefaultSidebarFieldSpecs();
        const previousData = {
          application_id: editingItem.application_id,
          item_id: editingItem.item_id,
          brand_id: editingItem.brand_id || null,
          organization_id: editingItem.organization_id || null,
          scope: resolveScopeLabel(editingItem),
        };
        const nextData = {
          application_id: formData.application_id,
          item_id: formData.item_id,
          brand_id: formData.brand_id || null,
          organization_id: formData.organization_id || null,
          scope: resolveScopeLabel({ ...formData, scope }),
        };
        const changes = diffFields(previousData, nextData, fieldSpecs);
        const excludeDetails =
          isExcludeMode && !formData.brand_id && !formData.organization_id
            ? buildExcludeAssignmentDetails()
            : [];
        const itemName = resolveItemName(formData.item_id);
        const applicationName = resolveApplicationName(formData.application_id);

        await logAdminAction(
          buildSummary(
            'Default sidebar item',
            itemName || applicationName,
            'updated',
            changes.length
          ),
          {
            url: '/admin/default-items',
            method: 'POST',
            status: 200,
            category: 'drawer',
            action: 'update_default_sidebar_item',
            applicationId: formData.application_id,
            applicationName,
            itemId: formData.item_id,
            itemName,
            brandId: formData.brand_id || null,
            brandName: formData.brand_id
              ? resolveBrandName(formData.brand_id)
              : null,
            organizationId: formData.organization_id || null,
            organizationName: formData.organization_id
              ? resolveOrganizationName(formData.organization_id)
              : null,
            scope: resolveScopeLabel({ ...formData, scope }),
            changes,
            ...(excludeDetails.length ? { details: excludeDetails } : {}),
          }
        );
      } else {
        const payload: any = {
          id: generateUUID(),
          application_id: formData.application_id,
          item_id: formData.item_id,
          is_active: true,
          user_id: formData.user_id || null,
          brand_id: formData.brand_id || null,
          organization_id: formData.organization_id || null,
        };
        await apiClientV2.post('/admin/default-items', payload, { params });

        if (isExcludeMode && !formData.brand_id && !formData.organization_id) {
          const promises: Promise<any>[] = [];

          for (const brand of excludedBrands) {
            const selectedItemId = excludedBrandItems[brand.id];
            if (selectedItemId) {
              const existingItem = defaultItems.find(item =>
                String(item.brand_id) === String(brand.id) &&
                item.application_id === formData.application_id
              );

              const brandPayload = {
                id: existingItem ? existingItem.id : generateUUID(),
                application_id: formData.application_id,
                item_id: selectedItemId,
                is_active: true,
                user_id: null,
                brand_id: brand.id,
                organization_id: null,
              };

              promises.push(apiClientV2.post('/admin/default-items', brandPayload, { params }));
            }
          }

          for (const org of excludedOrganizations) {
            const selectedItemId = excludedOrgItems[org.id];
            if (selectedItemId) {
              const existingItem = defaultItems.find(item =>
                String(item.organization_id) === String(org.id) &&
                item.application_id === formData.application_id
              );

              const orgPayload = {
                id: existingItem ? existingItem.id : generateUUID(),
                application_id: formData.application_id,
                item_id: selectedItemId,
                is_active: true,
                user_id: null,
                brand_id: null,
                organization_id: org.id,
              };

              promises.push(apiClientV2.post('/admin/default-items', orgPayload, { params }));
            }
          }

          await Promise.all(promises);
        } else {
          await apiClientV2.post('/admin/default-items', payload, { params });

          if (isAutoScope && formData.item_id) {
            try {
              if (scope === 'brand' && formData.brand_id) {
                if (!isBrandInAccessList(formData.item_id, formData.brand_id)) {
                  await addBrandToAccessList(formData.brand_id, formData.item_id, user.id);
                }
              }

              if (scope === 'organization' && formData.organization_id) {
                if (!isOrganizationInAccessList(formData.item_id, formData.organization_id)) {
                  await addOrganizationToAccessList(formData.organization_id, formData.item_id, user.id);
                }
              }
            } catch (accessError) {
              console.error('Error updating access list:', accessError);
              Toastify('warning', 'Default item created but failed to update access list');
            }
          }
        }

        Toastify('success', 'Default sidebar item created successfully');

        const fieldSpecs = getDefaultSidebarFieldSpecs();
        const createdData = {
          application_id: formData.application_id,
          item_id: formData.item_id,
          brand_id: formData.brand_id || null,
          organization_id: formData.organization_id || null,
          scope: resolveScopeLabel({ ...formData, scope }),
        };
        const excludeDetails =
          isExcludeMode && !formData.brand_id && !formData.organization_id
            ? buildExcludeAssignmentDetails()
            : [];
        const itemName = resolveItemName(formData.item_id);
        const applicationName = resolveApplicationName(formData.application_id);

        await logAdminAction(
          buildSummary('Default sidebar item', itemName || applicationName, 'created'),
          {
            url: '/admin/default-items',
            method: 'POST',
            status: 200,
            category: 'drawer',
            action: 'create_default_sidebar_item',
            applicationId: formData.application_id,
            applicationName,
            itemId: formData.item_id,
            itemName,
            brandId: formData.brand_id || null,
            brandName: formData.brand_id
              ? resolveBrandName(formData.brand_id)
              : null,
            organizationId: formData.organization_id || null,
            organizationName: formData.organization_id
              ? resolveOrganizationName(formData.organization_id)
              : null,
            scope: resolveScopeLabel({ ...formData, scope }),
            details: [
              ...buildDetails(createdData, fieldSpecs, { skipEmpty: true }),
              ...excludeDetails,
            ],
          }
        );
      }
      setOpenDialog(false);
      setEditingItem(null);
      loadDefaultItems();
    } catch (error: any) {
      console.error('Error saving default item:', error);
      Toastify('error', 'A default sidebar item already exists for this application.');
      const itemName = resolveItemName(formData.item_id);
      const applicationName = resolveApplicationName(formData.application_id);
      await logAdminAction(
        `Failed to ${editingItem ? 'update' : 'create'} default sidebar item "${
          itemName || applicationName || 'Unknown'
        }": ${error?.response?.data?.detail || error?.message || 'Unknown error'}`,
        {
          url: '/admin/default-items',
          method: 'POST',
          status: error?.response?.status,
          category: 'drawer',
          action: editingItem
            ? 'update_default_sidebar_item'
            : 'create_default_sidebar_item',
          applicationId: formData.application_id,
          applicationName,
          itemId: formData.item_id,
          itemName,
          brandId: formData.brand_id || null,
          brandName: formData.brand_id
            ? resolveBrandName(formData.brand_id)
            : null,
          organizationId: formData.organization_id || null,
          organizationName: formData.organization_id
            ? resolveOrganizationName(formData.organization_id)
            : null,
        },
        true
      );
    } finally {
      setSubmitting(false);
    }
  };

  const handleScopeChange = (scope: 'brand_id' | 'organization_id', value: string) => {
    setFormData(prev => ({
      ...prev,
      user_id: '',
      brand_id: scope === 'brand_id' ? value : '',
      organization_id: scope === 'organization_id' ? value : '',
    }));
  };

  // Pagination calculations
  const paginatedData = useMemo(() => {
    return defaultItems;
  }, [defaultItems]);

  const totalPages = Math.ceil(totalCount / rowsPerPage);

  const handleChangePage = (newPage: number) => {
    setPage(newPage);
    const startIndex = newPage * rowsPerPage;
    const endIndex = startIndex + rowsPerPage;
    const paginatedItems = allDefaultItems.slice(startIndex, endIndex);
    setDefaultItems(paginatedItems);

    const itemIds = paginatedItems.map((item: any) => item.item_id);
    loadItemDetails(itemIds);
  };

  const handleChangeRowsPerPage = (value: string) => {
    const newRowsPerPage = parseInt(value, 10);
    setRowsPerPage(newRowsPerPage);
    setPage(0);

    const startIndex = 0;
    const endIndex = newRowsPerPage;
    const paginatedItems = allDefaultItems.slice(startIndex, endIndex);
    setDefaultItems(paginatedItems);

    const itemIds = paginatedItems.map((item: any) => item.item_id);
    loadItemDetails(itemIds);
  };

  return (
    <div className="flex flex-col h-full">
      {/* Header with filters */}
      <div className="flex flex-wrap items-end gap-3 mb-3 flex-shrink-0">
        <div className="space-y-1.5 min-w-[160px]">
          <Label htmlFor="filter-application" className="text-sm font-medium">
            Application
          </Label>
          <SingleSelect
            options={[
              { label: "All applications", value: "all" },
              ...availableApplicationOptions,
            ]}
            defaultValue={filterApplicationId || "all"}
            onChange={(value) => {
              const val = value || "all";
              setFilterApplicationId(val === "all" ? "" : val);
              setFilterOrganizationId("");
              setFilterBrandId("");
            }}
            placeholder={loadingApplications ? "Loading..." : "All applications"}
            containerClassName="w-full"
          />
        </div>

        <div className="space-y-1.5 min-w-[160px]">
          <Label htmlFor="filter-organization" className="text-sm font-medium">
            Organization
          </Label>
          <SingleSelect
            options={[
              { label: "All organizations", value: "all" },
              ...availableOrganizationOptions,
            ]}
            defaultValue={filterOrganizationId || "all"}
            onChange={(value) => {
              const val = value || "all";
              setFilterOrganizationId(val === "all" ? "" : val);
              setFilterBrandId("");
            }}
            placeholder={loadingOrganizations ? "Loading..." : "All organizations"}
            containerClassName="w-full"
          />
        </div>

        <div className="space-y-1.5 min-w-[160px]">
          <Label htmlFor="filter-brand" className="text-sm font-medium">
            Brand
          </Label>
          <SingleSelect
            options={[
              { label: "All brands", value: "all" },
              ...availableBrandOptions,
            ]}
            defaultValue={filterBrandId || "all"}
            onChange={(value) => {
              const val = value || "all";
              setFilterBrandId(val === "all" ? "" : val);
            }}
            placeholder={loadingBrands ? "Loading..." : "All brands"}
            containerClassName="w-full"
          />
        </div>
      </div>

      {/* Table */}
      <div className="flex-1 min-h-0 flex flex-col">
        <DataTable
          className="flex-1 min-h-0 overflow-auto"
          maxHeight="100%"
          columns={(() => {
            const cols: TableColumn<DefaultSidebarItem>[] = [
              {
                key: 'application',
                header: 'Application',
                accessor: (item) => applications.find(app => app.id === item.application_id)?.name || item.application_id,
                width: '180px',
                render: (_v, item) => <span className="font-medium">{applications.find(app => app.id === item.application_id)?.name || item.application_id}</span>,
              },
              {
                key: 'organization',
                header: 'Organization',
                accessor: (item) => item.organization_id ? (organizations.find(o => String(o.id) === String(item.organization_id))?.name || item.organization_name || item.organization_id) : '--',
                width: '160px',
                render: (_v, item) => (
                  <span>
                    {item.organization_id ? (organizations.find(o => String(o.id) === String(item.organization_id))?.name || item.organization_name || item.organization_id) : '--'}
                  </span>
                ),
              },
              {
                key: 'brand',
                header: 'Brand',
                accessor: (item) => item.brand_id ? (brands.find(b => String(b.id) === String(item.brand_id))?.name || item.brand_name || item.brand_id) : '--',
                width: '140px',
                render: (_v, item) => (
                  <span>
                    {item.brand_id ? (brands.find(b => String(b.id) === String(item.brand_id))?.name || item.brand_name || item.brand_id) : '--'}
                  </span>
                ),
              },
              {
                key: 'item',
                header: 'Drawer Item',
                accessor: (item) => itemDetails[item.item_id]?.display_name || item.item_id,
                width: '200px',
              },
              {
                key: 'scope',
                header: 'Scope',
                accessor: (item) => item.user_id ? 'User' : item.brand_id ? 'Brand' : item.organization_id ? 'Organization' : 'Application-wide',
                width: '140px',
              },
              {
                key: 'status',
                header: 'Status',
                accessor: (item) => item.is_active,
                width: '100px',
                align: 'center',
                render: (_v, item) => (
                  <Badge variant={item.is_active ? 'default' : 'secondary'}>
                    {item.is_active ? 'Active' : 'Inactive'}
                  </Badge>
                ),
              },
            ];
            if (showActionsColumn) {
              cols.push({
                key: 'actions',
                header: 'Actions',
                accessor: (item) => item.id,
                width: '120px',
                align: 'center',
                render: (_v, item) => (
                  <div className="flex gap-1 justify-center">
                    {hasEditAccess && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleEdit(item)}
                        className="h-8 w-8 p-0"
                      >
                        <Edit className="h-4 w-4" />
                      </Button>
                    )}
                    {hasManageAccess && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => {
                          setItemToDelete(item);
                          setDeleteDialogOpen(true);
                        }}
                        className="h-8 w-8 p-0 text-red-600 hover:bg-red-50"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    )}
                  </div>
                ),
              });
            }
            return cols;
          })()}
          data={paginatedData}
          loading={loadingDefaultItems}
          emptyMessage="No default items found."
          stickyHeader
          showActions={false}
          pagination={{
            currentPage: page + 1,
            totalPages: Math.max(1, totalPages),
            pageSize: rowsPerPage,
            totalItems: totalCount,
            onPageChange: (p) => handleChangePage(p - 1),
            onPageSizeChange: (size) => handleChangeRowsPerPage(String(size)),
            pageSizeOptions: [10, 25, 50],
          }}
        />
      </div>

      {/* Create/Edit Sheet */}
      <Sheet open={openDialog} onOpenChange={(open) => {
        if (!open) {
          setOpenDialog(false);
          setEditingItem(null);
          setFormData({ application_id: '', item_id: '' });
          setErrors({});
          setScope('');
          setIsAutoScope(false);
          setAutoScopeMessage('');
          setIsExcludeMode(false);
          setExcludedBrands([]);
          setExcludedOrganizations([]);
          setExcludedBrandItems({});
          setExcludedOrgItems({});
        }
      }}>
        <SheetContent
          side="right"
          className="w-full sm:max-w-2xl flex flex-col h-full"
          onPointerDownOutside={(e) => e.preventDefault()}
          onInteractOutside={(e) => e.preventDefault()}
        >
          <SheetHeader>
            <SheetTitle>
              {editingItem ? 'Edit Default Sidebar Item' : 'Create Default Sidebar Item'}
            </SheetTitle>
          </SheetHeader>
          <div className="flex-1 overflow-y-auto min-h-0">
          <div className="space-y-4 py-4">
            {/* Application Select */}
            <div className="space-y-2">
              <Label htmlFor="application">Application *</Label>
              <SingleSelect
                options={applications.map((app) => ({
                  label: app.display_name || app.name,
                  value: String(app.id),
                }))}
                defaultValue={formData.application_id ? String(formData.application_id) : undefined}
                onChange={(value) => {
                  const val = value || "";
                  setFormData((prev) => ({ ...prev, application_id: val, item_id: "" }));
                }}
                placeholder={loadingApplications ? "Loading..." : "Select application"}
                containerClassName={`w-full ${errors.application_id ? "border-red-500" : ""}`}
              />
              {loadingApplications && (
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <SpinnerLoader className="h-4 w-4" />
                  <span>Loading applications...</span>
                </div>
              )}
            </div>

            {/* Item Select */}
            {formData.application_id && (
              <div className="space-y-2">
                <Label htmlFor="item">Item *</Label>
                <SingleSelect
                  options={items.map((option) => {
                    const itemDetail = itemDetails[option.id];
                    let accessInfo = "";
                    if (itemDetail?.access) {
                      const access = itemDetail.access;
                      if (access.brand_level?.brands?.length > 0) {
                        const brandMode = access.brand_level.access_mode;
                        const brandNames = access.brand_level.brands
                          .map((brand: any) => brand.name)
                          .join(", ");
                        accessInfo += ` [Brand ${brandMode}: ${brandNames}]`;
                      }
                      if (access.organization_level?.organizations?.length > 0) {
                        const orgMode = access.organization_level.access_mode;
                        const orgNames = access.organization_level.organizations
                          .map((org: any) => org.name)
                          .join(", ");
                        accessInfo += ` [Org ${orgMode}: ${orgNames}]`;
                      }
                    }
                    return {
                      label: `${option.display_name || option.name}${accessInfo ? ` ${accessInfo}` : ""}`,
                      value: String(option.id),
                    };
                  })}
                  defaultValue={formData.item_id ? String(formData.item_id) : undefined}
                  onChange={(value) => {
                    const newItemId = value || "";
                    setFormData((prev) => ({ ...prev, item_id: newItemId }));
                    if (newItemId) {
                      checkItemAccessAndSetScope(newItemId);
                    } else {
                      setIsAutoScope(false);
                      setAutoScopeMessage("");
                      setIsExcludeMode(false);
                      setExcludedBrands([]);
                      setExcludedOrganizations([]);
                      setExcludedBrandItems({});
                      setExcludedOrgItems({});
                    }
                  }}
                  placeholder={loadingItems ? "Loading..." : "Select item"}
                  containerClassName={`w-full ${errors.item_id ? "border-red-500" : ""}`}
                />
                {loadingItems && (
                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    <SpinnerLoader className="h-4 w-4" />
                    <span>Loading items...</span>
                  </div>
                )}
              </div>
            )}

            {/* Error for scope */}
            {errors.scope && (
              <div className="flex items-center gap-2 p-3 rounded-md bg-red-50 border border-red-200 text-red-700 text-sm">
                <AlertCircle className="h-4 w-4 flex-shrink-0" />
                <span>{errors.scope}</span>
              </div>
            )}

            {/* Note */}
            <p className="text-sm text-muted-foreground">
              Note: The Organization and Brand dropdowns above can be used to set scope. Leave all blank for application-wide default.
            </p>

            {/* Auto scope message */}
            {(isAutoScope || isExcludeMode) && autoScopeMessage && (
              <div className="flex items-start gap-2 p-3 rounded-md bg-blue-50 border border-blue-200 text-blue-700 text-sm">
                <Info className="h-4 w-4 flex-shrink-0 mt-0.5" />
                <span>{autoScopeMessage}</span>
              </div>
            )}

            {/* Error for exclude items */}
            {errors.excludeItems && (
              <div className="flex items-center gap-2 p-3 rounded-md bg-red-50 border border-red-200 text-red-700 text-sm">
                <AlertCircle className="h-4 w-4 flex-shrink-0" />
                <span>{errors.excludeItems}</span>
              </div>
            )}

            {/* Scope Select */}
            <div className="space-y-2">
              <Label htmlFor="scope">Scope (optional)</Label>
              <SingleSelect
                options={[
                  { label: "None", value: "none" },
                  { label: "Brand", value: "brand" },
                  { label: "Organization", value: "organization" },
                ]}
                defaultValue={scope || "none"}
                onChange={(val) => {
                  const value = val || "none";
                  const newScope = value === "none" ? "" : value;
                  setScope(newScope);
                  setIsAutoScope(false);
                  setAutoScopeMessage("");
                  setFormData((prev) => ({ ...prev, brand_id: "", organization_id: "", user_id: "" }));
                }}
                placeholder="Select scope"
                containerClassName="w-full"
              />
              {isAutoScope && (
                <p className="text-sm text-muted-foreground">
                  Scope is automatically determined based on item access configuration
                </p>
              )}
              {isExcludeMode && !isAutoScope && (
                <p className="text-sm text-muted-foreground">
                  Item has exclude mode access configuration. Select and choose specific entity to create targeted default, or leave empty for excluded entities options.
                </p>
              )}
            </div>

            {/* Brand Select */}
            {scope === 'brand' && (
              <div className="space-y-2">
                <Label htmlFor="brand">{isAutoScope ? "Brand *" : "Brand (optional)"}</Label>
                <SingleSelect
                  options={getAvailableBrands().map((brand) => ({
                    label: brand.name,
                    value: String(brand.id),
                  }))}
                  defaultValue={formData.brand_id ? String(formData.brand_id) : undefined}
                  onChange={(val) => setFormData((prev) => ({ ...prev, brand_id: val || "" }))}
                  placeholder={loadingBrands ? "Loading..." : "Select brand"}
                  containerClassName={`w-full ${errors.brand_id ? "border-red-500" : ""}`}
                />
                {loadingBrands && (
                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    <SpinnerLoader className="h-4 w-4" />
                    <span>Loading brands...</span>
                  </div>
                )}
                {errors.brand_id && (
                  <p className="text-sm text-red-500">{errors.brand_id}</p>
                )}
              </div>
            )}

            {/* Organization Select */}
            {scope === 'organization' && (
              <div className="space-y-2">
                <Label htmlFor="organization">{isAutoScope ? "Organization *" : "Organization (optional)"}</Label>
                <SingleSelect
                  options={getAvailableOrganizations().map((org) => ({
                    label: org.name,
                    value: String(org.id),
                  }))}
                  defaultValue={formData.organization_id ? String(formData.organization_id) : undefined}
                  onChange={(val) => setFormData((prev) => ({ ...prev, organization_id: val || "" }))}
                  placeholder={loadingOrganizations ? "Loading..." : "Select organization"}
                  containerClassName={`w-full ${errors.organization_id ? "border-red-500" : ""}`}
                />
                {loadingOrganizations && (
                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    <SpinnerLoader className="h-4 w-4" />
                    <span>Loading organizations...</span>
                  </div>
                )}
                {errors.organization_id && (
                  <p className="text-sm text-red-500">{errors.organization_id}</p>
                )}
              </div>
            )}

            {/* Excluded Entities Section */}
            {isExcludeMode && !formData.brand_id && !formData.organization_id && (
              <div className="mt-4 p-4 border rounded-md space-y-4">
                <h3 className="text-lg font-semibold">
                  Select Items for Excluded Entities
                </h3>

                {excludedBrands.length > 0 && (
                  <div className="space-y-3">
                    <p className="font-semibold text-sm">Excluded Brands:</p>
                    {excludedBrands.map((brand) => {
                      const availableItems = getAvailableItemsForBrand(brand.id);
                      return (
                        <div key={brand.id} className="space-y-2">
                          <Label htmlFor={`brand-item-${brand.id}`}>Item for {brand.name} (Brand)</Label>
                          <SingleSelect
                            options={availableItems.map((option) => {
                              const itemDetail = itemDetails[option.id];
                              let accessInfo = "";
                              if (itemDetail?.access) {
                                const access = itemDetail.access;
                                if (access.brand_level?.brands?.length > 0) {
                                  const brandMode = access.brand_level.access_mode;
                                  const brandNames = access.brand_level.brands
                                    .map((b: any) => b.name)
                                    .join(", ");
                                  accessInfo += ` [Brand ${brandMode}: ${brandNames}]`;
                                }
                                if (access.organization_level?.organizations?.length > 0) {
                                  const orgMode = access.organization_level.access_mode;
                                  const orgNames = access.organization_level.organizations
                                    .map((o: any) => o.name)
                                    .join(", ");
                                  accessInfo += ` [Org ${orgMode}: ${orgNames}]`;
                                }
                              }
                              return {
                                label: `${option.display_name || option.name}${accessInfo}`,
                                value: String(option.id),
                              };
                            })}
                            defaultValue={excludedBrandItems[brand.id] || undefined}
                            onChange={(val) =>
                              setExcludedBrandItems((prev) => ({ ...prev, [brand.id]: val || "" }))
                            }
                            placeholder="Select item"
                            containerClassName="w-full"
                          />
                        </div>
                      );
                    })}
                  </div>
                )}

                {excludedOrganizations.length > 0 && (
                  <div className="space-y-3">
                    <p className="font-semibold text-sm">Excluded Organizations:</p>
                    {excludedOrganizations.map((org) => {
                      const availableItems = getAvailableItemsForOrganization(org.id);
                      return (
                        <div key={org.id} className="space-y-2">
                          <Label htmlFor={`org-item-${org.id}`}>Item for {org.name} (Organization)</Label>
                          <SingleSelect
                            options={availableItems.map((option) => {
                              const itemDetail = itemDetails[option.id];
                              let accessInfo = "";
                              if (itemDetail?.access) {
                                const access = itemDetail.access;
                                if (access.brand_level?.brands?.length > 0) {
                                  const brandMode = access.brand_level.access_mode;
                                  const brandNames = access.brand_level.brands
                                    .map((b: any) => b.name)
                                    .join(", ");
                                  accessInfo += ` [Brand ${brandMode}: ${brandNames}]`;
                                }
                                if (access.organization_level?.organizations?.length > 0) {
                                  const orgMode = access.organization_level.access_mode;
                                  const orgNames = access.organization_level.organizations
                                    .map((o: any) => o.name)
                                    .join(", ");
                                  accessInfo += ` [Org ${orgMode}: ${orgNames}]`;
                                }
                              }
                              return {
                                label: `${option.display_name || option.name}${accessInfo}`,
                                value: String(option.id),
                              };
                            })}
                            defaultValue={excludedOrgItems[org.id] || undefined}
                            onChange={(val) =>
                              setExcludedOrgItems((prev) => ({ ...prev, [org.id]: val || "" }))
                            }
                            placeholder="Select item"
                            containerClassName="w-full"
                          />
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            )}
          </div>
          </div>
          <SheetFooter className="flex gap-2 pt-4 border-t flex-shrink-0 mt-auto">
            <Button onClick={() => setOpenDialog(false)} disabled={submitting} variant="outline">Cancel</Button>
            <Button onClick={handleSubmit} disabled={submitting}>
              {submitting ? (editingItem ? 'Updating...' : 'Creating...') : (editingItem ? 'Update' : 'Create')}
            </Button>
          </SheetFooter>
        </SheetContent>
      </Sheet>

      {/* Delete confirmation - lift-ui Dialog */}
      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete Default Sidebar Item</DialogTitle>
          </DialogHeader>
          <p className="text-muted-foreground py-2">Are you sure you want to delete this default sidebar item?</p>
          <DialogFooter className="flex gap-2 pt-4">
            <Button onClick={() => { setDeleteDialogOpen(false); setItemToDelete(null); }} variant="outline">Cancel</Button>
            <Button variant="destructive" onClick={async () => {
              if (itemToDelete) {
                try {
                  const user = JSON.parse(localStorage.getItem('user') || '{}');
                  const params: any = {
                    user_id: user.id,
                    is_admin: user.is_admin || false,
                    is_team: user.is_team || false,
                  };

                  if (user.brand_id !== undefined && user.brand_id !== null && user.brand_id !== "") {
                    params.brand_id = user.brand_id;
                  }

                  const res = await apiClientV2.delete(
                    `/admin/default-items/${itemToDelete.id}`,
                    { params }
                  );
                  Toastify('success', 'Default sidebar item deleted');

                  const fieldSpecs = getDefaultSidebarFieldSpecs();
                  const deletedData = {
                    application_id: itemToDelete.application_id,
                    item_id: itemToDelete.item_id,
                    brand_id: itemToDelete.brand_id || null,
                    organization_id: itemToDelete.organization_id || null,
                    scope: resolveScopeLabel(itemToDelete),
                  };
                  const itemName =
                    itemToDelete.item_name ||
                    resolveItemName(itemToDelete.item_id);
                  const applicationName =
                    itemToDelete.application_name ||
                    resolveApplicationName(itemToDelete.application_id);
                  const brandName =
                    itemToDelete.brand_name ||
                    (itemToDelete.brand_id
                      ? resolveBrandName(itemToDelete.brand_id)
                      : null);
                  const organizationName =
                    itemToDelete.organization_name ||
                    (itemToDelete.organization_id
                      ? resolveOrganizationName(itemToDelete.organization_id)
                      : null);

                  await logAdminAction(
                    buildSummary(
                      'Default sidebar item',
                      itemName || applicationName,
                      'deleted'
                    ),
                    {
                      url:
                        res.config?.url ||
                        `/admin/default-items/${itemToDelete.id}`,
                      method: (res.config?.method || 'DELETE').toUpperCase(),
                      status: res.status,
                      category: 'drawer',
                      action: 'delete_default_sidebar_item',
                      defaultSidebarItemId: itemToDelete.id,
                      applicationId: itemToDelete.application_id,
                      applicationName,
                      itemId: itemToDelete.item_id,
                      itemName,
                      brandId: itemToDelete.brand_id || null,
                      brandName,
                      organizationId: itemToDelete.organization_id || null,
                      organizationName,
                      details: buildDetails(deletedData, fieldSpecs, {
                        skipEmpty: true,
                      }),
                    }
                  );

                  setDeleteDialogOpen(false);
                  setItemToDelete(null);
                  loadDefaultItems();
                } catch (error: any) {
                  console.error('Error deleting default item:', error);
                  Toastify('error', 'Failed to delete default sidebar item');
                  const itemName =
                    itemToDelete?.item_name ||
                    resolveItemName(itemToDelete?.item_id);
                  const applicationName =
                    itemToDelete?.application_name ||
                    resolveApplicationName(itemToDelete?.application_id);
                  await logAdminAction(
                    `Failed to delete default sidebar item "${
                      itemName || applicationName || itemToDelete?.id || 'Unknown'
                    }": ${
                      error?.response?.data?.detail ||
                      error?.message ||
                      'Unknown error'
                    }`,
                    {
                      url: `/admin/default-items/${itemToDelete?.id}`,
                      method: 'DELETE',
                      status: error?.response?.status,
                      category: 'drawer',
                      action: 'delete_default_sidebar_item',
                      defaultSidebarItemId: itemToDelete?.id,
                      applicationId: itemToDelete?.application_id,
                      applicationName,
                      itemId: itemToDelete?.item_id,
                      itemName,
                    },
                    true
                  );
                }
              }
            }}>Delete</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default DefaultSidebarItems; 
